/**
 * Visual Feedback Components for Drag & Drop
 *
 * Renders insertion indicators, parent highlights, and drag overlays.
 * Completely isolated from canvas logic.
 */

import React from 'react';
import { DragOverlay } from '@dnd-kit/core';
import type { DragState, DropTarget, Viewport } from './types';
import type { MindmapLayoutDirection, MindmapNode, MindmapNodeShape } from '@/features/mindmap/core/MindmapTypes';

// ============================================================================
// Drag Overlay (Ghost that follows cursor)
// ============================================================================

// Keep class names in sync with MindmapNode.tsx so the ghost visually matches
const BASE_NODE_CLASS =
  'relative min-h-[28px] min-w-[210px] px-2 py-1 inline-flex items-center justify-start text-xs font-normal tracking-tight transition-all duration-300 backdrop-blur-3xl';

const SHAPE_CONTAINER_CLASSES: Record<MindmapNodeShape, string> = {
  rounded: `${BASE_NODE_CLASS} rounded-[6px] border`,
  rectangle: `${BASE_NODE_CLASS} rounded-[0px] border`,
  pill: `${BASE_NODE_CLASS} rounded-[50px] border`,
  underline: `${BASE_NODE_CLASS} rounded-[0px] border !border-l-transparent !border-t-transparent !border-r-transparent !px-1 !pl-2`,
};

const DEFAULT_TEXT_CLASS = 'text-gray-900 dark:text-gray-100';
const DEFAULT_BG_CLASS = 'bg-white dark:bg-gray-900';
const DEFAULT_BORDER_CLASS = 'border-gray-300 dark:border-gray-700';

interface MindmapDragOverlayProps {
  dragState: DragState | null;
  nodes: MindmapNode[];
  nodeDimensions: Map<string, { width: number; height: number }>;
  viewport: Viewport;
}

export function MindmapDragOverlay({ dragState, nodes, nodeDimensions, viewport }: MindmapDragOverlayProps) {
  if (!dragState) return null;

  const node = nodes.find((n) => n.id === dragState.draggedId);
  if (!node) return null;

  // Pull measured size; fallback to conservative defaults
  // If we have a DOM clone, render it directly for a perfect match
  const hasClone = !!dragState.ghostClone;
  const dims = dragState.ghostSize ?? nodeDimensions.get(node.id) ?? { width: 128, height: 40 };

  // Inline styles derived from node data to match visuals
  const inlineStyle: React.CSSProperties = {};
  if (node.bgColor) inlineStyle.backgroundColor = node.bgColor;
  if (node.textColor) inlineStyle.color = node.textColor;
  if (node.borderColor) inlineStyle.borderColor = node.borderColor;
  if (node.borderStyle) inlineStyle.borderStyle = node.borderStyle as any;
  if (typeof node.borderWidth === 'number') inlineStyle.borderWidth = node.borderWidth;
  if (node.borderRadius) inlineStyle.borderRadius = node.borderRadius as any;
  if (node.fontSize) inlineStyle.fontSize = node.fontSize as any;
  if (node.lineHeight) inlineStyle.lineHeight = node.lineHeight as any;
  if (node.boxShadow) inlineStyle.boxShadow = node.boxShadow as any;

  const shape: MindmapNodeShape = (node.shape ?? 'rounded') as MindmapNodeShape;
  const baseClass = SHAPE_CONTAINER_CLASSES[shape] ?? SHAPE_CONTAINER_CLASSES.rounded;
  const fallbackColors = [
    !node.textColor ? DEFAULT_TEXT_CLASS : '',
    !node.bgColor && shape !== 'underline' ? DEFAULT_BG_CLASS : '',
    !node.borderColor ? DEFAULT_BORDER_CLASS : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Wrapper that mounts the DOM clone, with fallback to styled div
  return (
    <DragOverlay dropAnimation={null}>
      {hasClone ? (
        <CloneMount clone={dragState.ghostClone!} width={dims.width} height={dims.height} zoom={viewport.zoom || 1} />
      ) : (
        <div
          className={`${baseClass} ${fallbackColors}`}
          style={{
            ...inlineStyle,
            boxSizing: 'border-box',
            minWidth: 0,
            maxWidth: 'none',
            minHeight: 0,
            width: Math.max(1, Math.round(dims.width / (viewport.zoom || 1))),
            height: Math.max(1, Math.round(dims.height / (viewport.zoom || 1))),
            transform: `scale(${viewport.zoom || 1})`,
            transformOrigin: 'top left',
            pointerEvents: 'none',
            opacity: 0.92,
            cursor: 'grabbing',
            userSelect: 'none',
          }}>
          {node.text}
        </div>
      )}
    </DragOverlay>
  );
}

// Mounts a provided DOM clone into the overlay
function CloneMount({
  clone,
  width,
  height,
  zoom = 1,
}: {
  clone: HTMLElement;
  width: number;
  height: number;
  zoom?: number;
}) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const host = containerRef.current;
    if (!host) return;
    // Ensure host is empty and non-interactive
    host.innerHTML = '';
    host.style.pointerEvents = 'none';
    // Lock container logical size inversely to zoom so scaled size matches snapshot
    const w = Math.max(1, Math.round(width / (zoom || 1)));
    const h = Math.max(1, Math.round(height / (zoom || 1)));
    host.style.width = `${w}px`;
    host.style.height = `${h}px`;
    host.style.transform = `scale(${zoom || 1})`;
    host.style.transformOrigin = 'top left';

    // Clone may already be styled; ensure it doesn't overflow
    const mounted = clone;
    try {
      mounted.style.pointerEvents = 'none';
      mounted.style.boxSizing = 'border-box';
      mounted.style.width = `${w}px`;
      mounted.style.height = `${h}px`;
    } catch {}
    host.appendChild(mounted);

    return () => {
      try {
        if (mounted.parentNode === host) host.removeChild(mounted);
      } catch {}
    };
  }, [clone, width, height, zoom]);

  return (
    <div
      ref={containerRef}
      style={{
        pointerEvents: 'none',
        opacity: 0.96,
        userSelect: 'none',
        display: 'inline-block',
      }}
    />
  );
}

// ============================================================================
// Insertion Indicator (Blue line showing where node will land)
// ============================================================================

interface InsertionIndicatorProps {
  dropTarget: DropTarget | null;
  layoutDirection: MindmapLayoutDirection;
  viewport: Viewport;
}

export function InsertionIndicator({ dropTarget, layoutDirection, viewport }: InsertionIndicatorProps) {
  if (!dropTarget) return null;

  const isHorizontal = layoutDirection === 'right' || layoutDirection === 'left';
  const rect = dropTarget.rect;

  // Transform from flow coordinates to screen coordinates
  const screenLeft = viewport.x + rect.x * viewport.zoom;
  const screenTop = viewport.y + rect.y * viewport.zoom;
  const screenWidth = rect.width * viewport.zoom;
  const screenHeight = rect.height * viewport.zoom;

  // Make the line more visible (minimum 2px)
  const lineThickness = Math.max(2, 3 * viewport.zoom);

  return (
    <div
      data-testid='mindmap-insertion-line'
      style={{
        position: 'absolute',
        left: screenLeft,
        top: screenTop,
        width: isHorizontal ? screenWidth : lineThickness,
        height: isHorizontal ? lineThickness : screenHeight,
        background: 'rgba(59,130,246,0.85)',
        boxShadow: '0 0 8px rgba(59,130,246,0.6)',
        borderRadius: 999,
        pointerEvents: 'none',
        zIndex: 1000,
        transition: 'all 100ms ease-out',
      }}
    />
  );
}

// Parent Highlight (Dashed glow around potential parent)
// ============================================================================

interface ParentHighlightProps {
  dropTarget: DropTarget | null;
  nodeDimensions: Map<string, { width: number; height: number }>;
  nodePositions: Map<string, { x: number; y: number }>;
  viewport: Viewport;
}

export function ParentHighlight({ dropTarget, nodeDimensions, nodePositions, viewport }: ParentHighlightProps) {
  if (!dropTarget || dropTarget.type !== 'parent-zone' || !dropTarget.targetNodeId) {
    return null;
  }

  const position = nodePositions.get(dropTarget.targetNodeId);
  const dimensions = nodeDimensions.get(dropTarget.targetNodeId);

  if (!position || !dimensions) return null;

  // Transform to screen coordinates
  const padding = 10 / viewport.zoom;
  const screenLeft = viewport.x + (position.x - padding) * viewport.zoom;
  const screenTop = viewport.y + (position.y - padding) * viewport.zoom;
  const screenWidth = (dimensions.width + padding * 2) * viewport.zoom;
  const screenHeight = (dimensions.height + padding * 2) * viewport.zoom;

  return (
    <div
      data-testid='mindmap-parent-highlight'
      style={{
        position: 'absolute',
        left: screenLeft,
        top: screenTop,
        width: screenWidth,
        height: screenHeight,
        border: '2px dashed rgba(59,130,246,0.6)',
        background: 'rgba(59,130,246,0.1)',
        borderRadius: 8,
        pointerEvents: 'none',
        zIndex: 999,
        animation: 'pulse 2s ease-in-out infinite',
      }}
    />
  );
}

// ============================================================================
// Combined Feedback Renderer
// ============================================================================

interface DragFeedbackProps {
  dragState: DragState | null;
  nodes: MindmapNode[];
  nodeDimensions: Map<string, { width: number; height: number }>;
  nodePositions: Map<string, { x: number; y: number }>;
  layoutDirection: MindmapLayoutDirection;
  viewport: Viewport;
}

/**
 * Renders all drag & drop visual feedback.
 * Use this as a single component to manage all D&D visuals.
 */
export function DragFeedback({
  dragState,
  nodes,
  nodeDimensions,
  nodePositions,
  layoutDirection,
  viewport,
}: DragFeedbackProps) {
  return (
    <>
      {/* Parent highlight (behind insertion line) */}
      <ParentHighlight
        dropTarget={dragState?.dropTarget ?? null}
        nodeDimensions={nodeDimensions}
        nodePositions={nodePositions}
        viewport={viewport}
      />

      {/* Insertion line only for child mode (parent-zone drops) */}
      {dragState?.dropTarget?.type === 'parent-zone' && (
        <InsertionIndicator
          dropTarget={dragState?.dropTarget ?? null}
          layoutDirection={layoutDirection}
          viewport={viewport}
        />
      )}

      {/* Drag overlay (always on top, outside ReactFlow) */}
      <MindmapDragOverlay dragState={dragState} nodes={nodes} nodeDimensions={nodeDimensions} viewport={viewport} />
    </>
  );
}

// Add pulse animation via global styles (injected by parent component)
export const dragFeedbackStyles = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.01);
    }
  }
`;
